import { computed, inject, Injectable, resource } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SettingEntityType, SwitchMode } from './enums';
import { IGetParentSettingRequest } from '@azavista/servicelib';
import { SettingsService } from './settings.service';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { filter } from 'rxjs';
import { OnsiteCheckInClientAuthService } from './components/onsite-checkin-client/onsite-checkin-client-auth.service';
import { delay } from '@azavista/components/shared';

export type UserPreference = {
    preferredEventIds?: string[];
    kioskCheckInMode?: SwitchMode;
};

// Default values for user preference properties when not set.
const USER_PREFERENCE_DEFAULTS: Partial<UserPreference> = {
    kioskCheckInMode: SwitchMode.badgePrintMode,
};

@Injectable({
    providedIn: 'root',
})
export class UserPreferenceService {
    private readonly authSvc = inject(OnsiteCheckInClientAuthService);
    private readonly settingsSvc = inject(SettingsService);

    private readonly parentSettingsResource = resource({
        params: () => {
            return this.authSvc.user$()?.id ?? undefined
        },
        loader: ({ params: userId }) =>
            this.settingsSvc.getParentSetting<UserPreference>(
                this.getParentSettingsReq(userId),
            ),
    });

    isParentSettingsResourceStatus$ = toObservable(
        this.parentSettingsResource.status
    );
    userIdNotEmpty$ = toObservable(
        this.authSvc.user$
    ).pipe(filter(user => !!user?.id));


    // Returns the preference value, or the default for that property when not set.
    private getPropertyWithDefault<PropertyName extends keyof UserPreference>(
        property: PropertyName,
    ) {
        const value = this.parentSettingsResource.value()?.setting?.[property];
        return (value ?? USER_PREFERENCE_DEFAULTS[property]) as UserPreference[PropertyName];
    }

    getPropertyValue<PropertyName extends keyof UserPreference>(
        property: PropertyName,
    ) {
        return this.getPropertyWithDefault(property);
    }

    getPropertySignal<PropertyName extends keyof UserPreference>(
        property: PropertyName,
    ) {
        return computed(() => this.getPropertyWithDefault(property));
    }

    async updateProperty<PropertyName extends keyof UserPreference>(
        property: PropertyName,
        value: UserPreference[PropertyName],
    ) {
        const currentData = await this.getCurrentSetting();
        if (currentData?.[property] === value) {
            return;
        }
        const patch: Partial<UserPreference> = {
            [property]: value,
        };
        await this.patchParentSettings(patch);
    }

    async patchParentSettings(patch: Partial<UserPreference>) {
        const user = await firstValueFrom(this.userIdNotEmpty$);
        const currentData = await this.getCurrentSetting();
        await this.settingsSvc.saveParentSetting({
            ...this.getParentSettingsReq(user.id),
            setting: {
                ...(currentData || {}),
                ...patch,
            },
        });
        await delay(300);
        const isReloaded = this.parentSettingsResource.reload();
        return isReloaded;
    }

    private async getCurrentSetting() {
        if (this.parentSettingsResource.status() !== 'resolved') {

            // Wait for the first time it emits 'false'
            await firstValueFrom(
                this.isParentSettingsResourceStatus$.pipe(
                    filter((status) => status === 'resolved')
                )
            );
        }
        const currentData = this.parentSettingsResource.value()?.setting;
        return currentData;
    }

    private getParentSettingsReq(userId: number): IGetParentSettingRequest {
        return {
            entityType: SettingEntityType.user,
            parentType: 'user',
            parentEntityId: `${userId}`,
            settingType: 'preferences',
        };
    }
}
